import { useState, useEffect } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import LoadBonusPromo from './LoadBonusPromo';

/**
 * Reusable Play-It-Back confirmation dialog.
 *
 * Props:
 *   open           : boolean
 *   onOpenChange   : (open) => void
 *   maxAmount      : number (remaining cashout amount)
 *   playerName     : string (display only)
 *   isAdmin        : bool (shows note field)
 *   busy           : bool
 *   onConfirm      : ({ amount, note }) => Promise
 */
export const PlayItBackDialog = ({
    open,
    onOpenChange,
    maxAmount = 0,
    playerName = '',
    isAdmin = false,
    busy = false,
    onConfirm,
}) => {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [touched, setTouched] = useState(false);
    const [confirmFull, setConfirmFull] = useState(false);

    // Reset when re-opened. Depend on `open` only — the dialog fully
    // closes/reopens per row, so we don't need `maxAmount` here, and keeping it
    // out means the parent's poll (which can hand us a new maxAmount) can never
    // wipe what the admin is typing mid-entry.
    useEffect(() => {
        if (open) {
            setAmount('');
            setNote('');
            setTouched(false);
            setConfirmFull(false);
        }
    }, [open]);

    const parsed = parseFloat(amount);
    const isValid = !isNaN(parsed) && parsed > 0 && parsed <= maxAmount + 0.001;
    const isFull = isValid && Math.abs(parsed - maxAmount) < 0.01;
    const remainingAfter = isValid ? Math.max(0, maxAmount - parsed) : maxAmount;

    const quickAmounts = [
        { label: '25%', value: maxAmount * 0.25 },
        { label: '50%', value: maxAmount * 0.5 },
        { label: '75%', value: maxAmount * 0.75 },
        { label: 'All',  value: maxAmount },
    ].filter((q) => q.value >= 1);

    const handleSubmit = async () => {
        if (!isValid || busy) return;
        // Safety net: require explicit confirmation for a FULL reversal so
        // it can never happen by accident (this was the source of the
        // "selected $20, the whole thing got refunded" bug).
        if (isFull && !confirmFull) {
            setConfirmFull(true);
            return;
        }
        await onConfirm({
            amount: isFull ? null : Math.round(parsed * 100) / 100,
            note: note.trim() || undefined,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md" data-testid="play-it-back-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white text-xl flex items-center gap-2">
                        <RotateCcw className="w-5 h-5 text-[#00E0FF]" />
                        Cancel &amp; Play It Back
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        {isAdmin ? (
                            <>How much of <span className="text-white font-bold">{playerName || 'this player'}'s</span> ${maxAmount.toFixed(2)} cashout should go back as playable credits?</>
                        ) : (
                            <>How much of your ${maxAmount.toFixed(2)} cashout do you want back in your balance to keep playing?</>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <LoadBonusPromo compact context="playback" />

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-white mb-2">
                            Amount to play back
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] font-mono">$</span>
                            <Input
                                type="number"
                                min="0.01"
                                max={maxAmount}
                                step="0.01"
                                value={amount}
                                onChange={(e) => { setAmount(e.target.value); setTouched(true); }}
                                className="pl-7 bg-black/40 border-white/10 text-white text-2xl font-mono font-bold h-14"
                                data-testid="play-it-back-amount-input"
                                autoFocus
                            />
                        </div>
                        <p className="text-[10px] text-[#A0AAB2] mt-1">
                            Max ${maxAmount.toFixed(2)}
                            {isValid && !isFull && (
                                <span className="text-[#FFD700]"> · ${remainingAfter.toFixed(2)} will stay queued for payout</span>
                            )}
                            {isValid && isFull && (
                                <span className="text-[#39FF14]"> · Full cancellation — entire cashout returned</span>
                            )}
                        </p>
                    </div>

                    {quickAmounts.length > 0 && (
                        <div className="flex gap-2">
                            {quickAmounts.map((q) => (
                                <button
                                    type="button"
                                    key={q.label}
                                    onClick={() => { setAmount(q.value.toFixed(2)); setTouched(true); }}
                                    className="flex-1 py-2 rounded-md bg-white/5 hover:bg-[#00E0FF]/20 text-white text-xs font-bold border border-white/10 hover:border-[#00E0FF]/40 transition-colors"
                                    data-testid={`play-it-back-quick-${q.label.toLowerCase()}`}
                                >
                                    {q.label}
                                    <div className="font-mono text-[10px] text-[#A0AAB2]">${q.value.toFixed(2)}</div>
                                </button>
                            ))}
                        </div>
                    )}

                    {isAdmin && (
                        <div>
                            <label className="block text-sm font-bold text-white mb-2">
                                Note (optional) — visible in audit log
                            </label>
                            <Input
                                type="text"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="e.g. Player asked at the counter"
                                className="bg-black/40 border-white/10 text-white"
                                data-testid="play-it-back-note-input"
                            />
                        </div>
                    )}

                    {touched && !isValid && parsed > maxAmount && (
                        <p className="text-[#FF3B30] text-xs font-bold">Can only play back up to ${maxAmount.toFixed(2)}</p>
                    )}

                    {confirmFull && isFull && (
                        <div className="rounded-md border border-[#FFD700]/40 bg-[#FFD700]/10 p-3" data-testid="play-it-back-confirm-full">
                            <p className="text-[#FFD700] text-sm font-bold">⚠ This will refund the entire ${maxAmount.toFixed(2)} cashout.</p>
                            <p className="text-[#FFD700]/80 text-xs mt-1">Click "Play Back ${maxAmount.toFixed(2)}" again to confirm, or change the amount to do a partial.</p>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        className="border-white/10 text-white hover:bg-white/5"
                        data-testid="play-it-back-cancel"
                    >
                        Never mind
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!isValid || busy}
                        className={`font-bold ${isFull && confirmFull ? 'bg-[#FFD700] text-black hover:bg-[#FFD700]/80' : 'bg-[#00E0FF] text-black hover:bg-[#00E0FF]/80'}`}
                        data-testid="play-it-back-confirm"
                    >
                        {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                {isFull && confirmFull
                                    ? `Yes — Refund all $${maxAmount.toFixed(2)}`
                                    : `Play Back $${(parseFloat(amount) || 0).toFixed(2)}`}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PlayItBackDialog;
